import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { DroTabState } from 'projects/shared/src/lib/modules/dro/model/dro.model';
import { DroService } from 'projects/shared/src/lib/modules/dro/services/dro.service';
import { isDroReadingOutstanding } from 'projects/shared/src/lib/modules/dro/utils/dro-validation';
import { serviceOrderStatuses } from '../models/service-order.model';

/** Statuses in which a reading can be created from the service order (spec 4.7); every other status is read-only. */
const DRO_CAPTURE_STATUSES: string[] = [serviceOrderStatuses.enroute, serviceOrderStatuses.inProgress, serviceOrderStatuses.paused];

/** Statuses in which Payment and Invoice are blocked while the reading is outstanding. */
const DRO_ENFORCED_STATUSES: string[] = [serviceOrderStatuses.inProgress];

const CHECK_FAILED_MESSAGE = 'Could not check the DRO reading. Please try again.';

export type DroCompletionCheck = 'proceed' | 'blocked' | 'goToDroTab';

interface DroServiceOrder {
  serviceOrderId?: string;
  serialNumber?: string;
  itemCode?: string;
  status?: string;
}

/** DRO rules specific to service orders, shared by the FST and ISC service order popups. */
@Injectable({
  providedIn: 'root'
})
export class ServiceOrderDroService {

  constructor(
    private droService: DroService,
    private commonService: CommonService
  ) { }

  canRecordReading(status: string): boolean {
    return DRO_CAPTURE_STATUSES.includes(status);
  }

  readOnlyReason(status: string): string {
    return `Readings can be recorded while the service order is En Route, In Progress or Paused; it is ${status || 'not yet saved'}.`;
  }

  /** The DRO tab is rendered only for a saved order whose equipment model is attached to an active form. */
  hasActiveForm(serviceOrder: DroServiceOrder): Observable<boolean> {
    if (!serviceOrder?.serviceOrderId || !serviceOrder.serialNumber || !serviceOrder.itemCode) {
      return of(false);
    }
    return this.droService.getActiveFormByItem(serviceOrder.itemCode, serviceOrder.serialNumber, serviceOrder.serviceOrderId)
      .pipe(map(res => !!(res.isSuccessful && res.result)));
  }

  /** Tab marker: a red dot while a required reading is outstanding, a green tick once this visit's reading is saved. */
  tabMarker(status: string, state: DroTabState | null): 'outstanding' | 'saved' | null {
    if (!state) {
      return null;
    }
    if (state.serviceOrderReadingId) {
      return 'saved';
    }
    return this.canRecordReading(status) && isDroReadingOutstanding(null, state.todayReadingId) ? 'outstanding' : null;
  }

  /**
   * Payment / Invoice gate. Resolves 'proceed' when the status does not enforce the check or the reading is in place;
   * otherwise shows the "DRO readings required" message and resolves 'blocked' or 'goToDroTab'.
   */
  checkBeforeCompletion(serviceOrder: DroServiceOrder, action: 'payment' | 'invoicing'): Promise<DroCompletionCheck> {
    if (!DRO_ENFORCED_STATUSES.includes(serviceOrder?.status) || !serviceOrder.serviceOrderId) {
      return Promise.resolve('proceed');
    }
    return new Promise(resolve => {
      this.droService.getServiceOrderReadingStatus(serviceOrder.serviceOrderId, serviceOrder.serialNumber, serviceOrder.itemCode).subscribe({
        next: res => {
          if (!res.isSuccessful || !res.result) {
            this.commonService.showNotification('error', res.message || CHECK_FAILED_MESSAGE);
            resolve('blocked');
          } else if (!res.result.outstanding) {
            resolve('proceed');
          } else {
            this.commonService.showConfirmation(
              `Record the DRO readings for serial <b>${serviceOrder.serialNumber}</b> before ${action === 'payment' ? 'taking payment for' : 'invoicing'} this service order.`,
              'Go to DRO tab', 'Cancel', 'DRO readings required'
            ).then(result => resolve(result.isConfirmed ? 'goToDroTab' : 'blocked'));
          }
        },
        error: () => {
          this.commonService.showNotification('error', CHECK_FAILED_MESSAGE);
          resolve('blocked');
        }
      });
    });
  }
}
